import {t} from "@lingui/core/macro";
import * as React from "react";

import {
    useIsMounted,
    useOnMountEffect,
    type StyleType,
} from "@khanacademy/wonder-blocks-core";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    markMinorConversions,
    usePublishAnalyticsEvent,
    type MinorConversionData,
} from "@khan/analytics-learning-time";
import {
    authType,
    calculateNextUrl,
    createReauthUrl,
    isDateConsideredOverAgeGate,
    LoginButton,
    reauthWithThirdParty,
    type AuthType,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {useGql} from "@khan/data-gql";
import Log from "@khan/logging";
import {preserveMarketingParams, redirect, useLocation} from "@khan/urls";

import {exchangeGoogleCodeForIDToken} from "./exchange-google-code-for-id-token.ts";
import {getErrorMessageByCode, loginWithGoogle} from "./google-graphql.ts";
import {GoogleIcon} from "./google-icon.tsx";
import {initCodeClient} from "./google-util.ts";
import type {CodeClient, CodeResponse} from "./types.ts";

export interface CompleteLoginOverrideParamsGoogle {
    userData: LoginTypeData;
}

interface Props {
    disabled?: boolean;
    // Whether the user is using Clever to authenticate.
    linkClever?: boolean;
    // The clever transfer token used to authenticate with clever,
    // the token is only needed if linkClever is true
    cleverTransferToken?: string;
    continueUrl?: string;
    role?: Role;
    size?: "small" | "medium" | "large";
    style?: StyleType;
    minorConversions?: ReadonlyArray<MinorConversionData>;
    classCodes?: Array<string>;
    birthdate?: string;
    // An invite ID that a user is signing up with
    inviteId?: string;
    // Whether we want to allow the user to link a shadowed Google account
    // (A shadowed Google account is one that is not yet associated with a Khan
    // account, but has the same email address as the Khan account.)
    disallowShadowLink?: boolean;
    // Callback for when doReauth succeeds.
    //
    // If not provided, we'll automatically redirect the window to the next
    // login step.
    //
    // But, if this *is* provided, we will *not* redirect automatically. You
    // can use this to trigger a different success behavior instead, or perform
    // the redirect yourself and also show a loading UI, for example.
    onSuccess?: (url: string) => unknown;
    // Callback for when doLogin succeeds, overrides the completeLogin behavior
    //
    // If this is not provided, completeLogin will be executed as normal
    //
    // If this is provided, the passed function will be executed instead of completeLogin
    //
    // If your override function requires additional parameters, add them to the completeLoginOverrideParams type
    completeLoginOverride?: (
        options: CompleteLoginOverrideParamsGoogle,
    ) => Promise<void>;

    // Callback to be called after the user is successfully logged in but before calling completeLogin
    // (or completeLoginOverride if provided)
    beforeCompleteLogin?: () => Promise<boolean>;

    /**
     * Callback for when the login fails.
     *
     * This can be caused due to Google auth errors or to errors caused in the
     * loginWithGoogle mutation.
     */
    onError?: (message: string) => unknown;
    // Whether this login button is for the reauthentication use case.
    reauth?: boolean;
    /**
     * Callback for when the reauth flow fails.
     *
     * This is executed when the reauthentication with Google fails.
     */
    onReauthError?: (url: string) => unknown;
    /**
     * Event Type for the CEDAR event which will be published on click
     *
     * We only pass the event type and rely on this component to determine
     * the rest of the event data.
     *
     * NOTE: If you update the list of possible values for this type, make sure
     * to update `handleClick` to publish the correct event.
     */
    cedarEventType?: "SignupGoogleButtonClicked";
    /**
     * Whether to show only the icon of the button
     */
    iconOnly?: boolean;
    /**
     * Whether form validation is required before executing the login logic
     */
    validationRequired?: boolean;
    /**
     * Whether the current form is valid. Determined by the TOS checkbox
     */
    isFormValid?: boolean;
    /**
     * Called when the form is invalid and validation is required.
     */
    onValidationError?: () => void;
    /**
     * Whether button is SIGNUP or LOGIN
     */
    context?: AuthType;
}

// A promise that resolves to a given Google Auth instance.
let googleAuthAPI: Promise<any> | null = null;

/**
 * Handles the UI part of the Google signIn integration.
 *
 * For more information about how the login flow works:
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/650773166/Sign+in+with+Google
 */
export function GoogleButton(props: Props): React.ReactNode {
    const location = useLocation();
    const gqlFetch = useGql();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const [error, setError] = React.useState<Error | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [loggingIn, setLoggingIn] = React.useState(false);
    const disableSignupViaOauthLoginFeatureFlagOn = useFlagOrCookie(
        "disable-signup-via-oauth-login",
    );

    const _isMounted = useIsMounted();

    useOnMountEffect(() => {
        preloadGoogleAuthApi();
    });

    const doLoginOrReauth: (idToken: string) => void = (idToken: string) => {
        props.reauth ? doReauth(idToken) : doLogin(idToken);
    };

    /**
     * Handle the response from Google's OAuth2 code flow.
     *
     * This is called when the user has been authenticated by Google and we have
     * a code to exchange for an ID token.
     *
     * NOTE: We need to exchange the code for an ID token because we need to
     * obtain the user's Google profile information (e.g. email, name, etc).
     *
     * @param {CodeResponse} response - The response from Google's OAuth2 code
     * flow.
     */
    const handleCodeResponse = (response: CodeResponse): void => {
        const code = response.code;
        exchangeGoogleCodeForIDToken(gqlFetch, code, location.origin).then(
            (idToken) => {
                // Notify the user via UI that it's being logged in.
                setLoading(true);
                doLoginOrReauth(idToken);
            },
        );
    };

    const handleGoogleAuthError = (error?: any): void => {
        // NOTE: Logging the error to the console to help debugging.
        // eslint-disable-next-line no-console -- see above
        console.log("Error with google auth", error);
        // https://developers.google.com/identity/sign-in/web/reference#error_codes
        if (error.error) {
            // Error loading the google iframe. Keep it in memory until
            // the user clicks the button.
            setError(new Error(error.error));
            setLoading(false);
        }
    };

    /**
     * Preloads Google API client JS.
     * NOTE: This is required to avoid having issues with Safari blocking popup
     * windows.
     *
     * @see https://webkit.org/blog/10218/full-third-party-cookie-blocking-and-more/
     * @see https://github.com/google/google-api-javascript-client/issues/589
     * @see https://developers.google.com/identity/sign-in/web/troubleshooting
     */
    const preloadGoogleAuthApi = (): void => {
        setLoading(true);

        googleAuthAPI = initCodeClient(handleCodeResponse)
            .then((client) => {
                setLoading(false);
                return client;
            })
            .catch(handleGoogleAuthError);
    };

    /**
     * Logs in case we get any error from the Google auth API or
     * from the GraphQL mutation.
     * @param {Error} error The error thrown in the current context.
     */
    const handleError = (error: Error): void => {
        const errorMessage = getErrorMessageByCode(error.message);

        // We don't log the CANNOT_CREATE_NEW_ACCOUNT error because it's an
        // expected error case.
        if (error.message !== "CANNOT_CREATE_NEW_ACCOUNT") {
            Log.log("Something unexpected happened!", {
                code: error.message,
                message: errorMessage,
            });
        }

        if (props.onError) {
            props.onError(errorMessage);
        }

        setError(null);
        setLoading(false);
        setLoggingIn(false);
    };

    const handleClick = (e: React.SyntheticEvent) => {
        if (props.validationRequired && !props.isFormValid) {
            // Trigger error callback for invalid form when validation is required
            props.onValidationError?.();
            return;
        }
        // Allow to using Google auth only when the gapi JS client script is
        // loaded.
        if (!googleAuthAPI) {
            return;
        }

        // If there is an error stored in memory, then notify the consumer in
        // order to handle such error.
        if (error) {
            handleError(error);
            return;
        }

        setLoading(true);

        if (props.cedarEventType === "SignupGoogleButtonClicked") {
            publishAnalyticsEvent(
                {
                    eventType: "SignupGoogleButtonClicked",
                    eventSchemaVersion: 1,
                    isUnder13:
                        !!props.role &&
                        !isDateConsideredOverAgeGate(
                            props.birthdate || "",
                            props.role,
                        ),
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
        }

        if (props.minorConversions) {
            markMinorConversions(props.minorConversions);
        }

        googleAuthAPI.then((client: CodeClient) => client.requestCode());
    };

    /**
     * Complete the login flow after we get the response from loginWithGoogle.
     *
     * @param {LoginTypeData} userData The login result data.
     */
    const completeLogin: (userData: LoginTypeData) => Promise<void> = async (
        userData: LoginTypeData,
    ) => {
        const continueUrl = props.continueUrl || "/";

        const calculatedNextUrl = await calculateNextUrl({
            gqlFetch: gqlFetch,
            location: location,
            loginType: "google",
            continueUrl,
            isFirstLogin: userData.isFirstLogin,
            joinClassrooms: userData.joinClassrooms,
            linkClever: props.linkClever,
        });
        redirect(
            preserveMarketingParams(
                calculatedNextUrl,
                new URLSearchParams(location.search),
            ),
        );
    };

    const doLogin = async (idToken: string) => {
        // Only pass context if signup check is enabled;
        // Skip "REAUTH" because it's not a valid flowType for the mutation
        const contextValue = disableSignupViaOauthLoginFeatureFlagOn
            ? props.context === authType.REAUTH
                ? undefined
                : props.context
            : undefined;

        const options = {
            token: idToken,
            signupCodes: props.classCodes,
            birthdate: props.birthdate,
            linkClever: props.linkClever,
            cleverTransferToken: props.cleverTransferToken,
            role: props.role,
            inviteId: props.inviteId,
            disallowShadowLink: props.disallowShadowLink,
            context: contextValue,
        } as const;

        try {
            const userData = await loginWithGoogle(gqlFetch, options);
            let succeeded = true;
            if (props.beforeCompleteLogin) {
                succeeded = await props.beforeCompleteLogin();
            }
            if (succeeded) {
                if (props.completeLoginOverride) {
                    props.completeLoginOverride({userData});
                } else {
                    completeLogin(userData);
                }
            }
        } catch (error: any) {
            if (
                error.message === "SHADOWS_EXISTING_ACCOUNT" &&
                !options.disallowShadowLink
            ) {
                // We redirect to /login/oauth/verify when we get this
                // error so show the loggingIn state instead of handling
                // the error as we normally would.
                setLoggingIn(true);
            } else {
                handleError(error);
            }
        }
    };

    const doReauth: (idToken: string) => void = (idToken: string) => {
        reauthWithThirdParty(gqlFetch, "google", idToken)
            .then((response) => {
                // get redirect URL that will display the success message.
                const reauthUrl = createReauthUrl({loginType: "google"});

                // execute onSuccess callback passed as a prop.
                // Otherwhise, redirect to the reauthUrl.
                props.onSuccess
                    ? props.onSuccess(reauthUrl)
                    : redirect(reauthUrl);

                if (!_isMounted) {
                    return;
                }

                setLoading(false);
            })
            .catch((error) => {
                handleError(error);
                // get redirect URL that will display the error message.
                const reauthUrlError = createReauthUrl({
                    loginType: "google",
                    error: error.message,
                });

                props.onReauthError
                    ? props.onReauthError(reauthUrlError)
                    : redirect(reauthUrlError);

                if (!_isMounted) {
                    return;
                }

                setLoading(false);
            });
    };

    const disabled = !googleAuthAPI || loading || !!props.disabled;
    return (
        <LoginButton
            title={!loggingIn ? t`Continue with Google` : t`Logging you in...`}
            iconComponent={GoogleIcon}
            disabled={disabled}
            onClick={handleClick}
            size={props.size}
            style={props.style}
            iconOnly={props.iconOnly}
        />
    );
}
